/**
 * The map's single toolbar (T4). It owns the row of tool buttons, the panel
 * that unfolds beneath it, and the rule that only one tool is open at a time.
 * It knows nothing about measuring or coordinates: callers add a tool and get
 * back an empty panel to fill.
 *
 * Closing is a deliberate toggle — its own button, or another tool taking its
 * place. A map click never closes anything, because measuring *is* clicking
 * the map (spec §3.2).
 */
export interface MapToolbar {
  /** Adds a toggle tool and returns its (initially hidden) panel to fill. */
  addTool(id: string, label: string, title?: string): HTMLElement
  /** Adds a button with no panel — an immediate action. */
  addAction(
    id: string,
    label: string,
    title: string,
    onClick: (button: HTMLButtonElement) => void,
  ): void
  /** Called when this tool stops being displayed (toggled off, or replaced). */
  onToolClose(id: string, cb: () => void): void
  /** Folds any open tool away. */
  close(): void
}

export function renderMapToolbar(host: HTMLElement): MapToolbar {
  const bar = document.createElement('div')
  bar.className = 'map-toolbar'
  bar.innerHTML = `<div class="map-toolbar__row"></div>`
  host.appendChild(bar)
  const row = bar.querySelector('.map-toolbar__row') as HTMLElement

  const panels = new Map<string, HTMLElement>()
  const buttons = new Map<string, HTMLButtonElement>()
  const closeHandlers = new Map<string, () => void>()
  let open: string | undefined

  function show(id: string | undefined): void {
    const previous = open
    open = id
    for (const [key, panel] of panels) {
      panel.hidden = key !== id
      const button = buttons.get(key)!
      button.setAttribute('aria-pressed', String(key === id))
      button.setAttribute('aria-expanded', String(key === id))
    }
    // Announce *after* the DOM settles, so a handler that reacts by touching
    // the map sees the final state.
    if (previous && previous !== id) closeHandlers.get(previous)?.()
  }

  function button(id: string, label: string, title: string | undefined): HTMLButtonElement {
    const el = document.createElement('button')
    el.type = 'button'
    el.id = `tool-${id}`
    el.className = 'map-toolbar__btn'
    el.setAttribute('aria-pressed', 'false')
    if (title) el.title = title
    el.textContent = label
    row.appendChild(el)
    return el
  }

  return {
    addTool(id, label, title) {
      const el = button(id, label, title)
      el.setAttribute('aria-expanded', 'false')
      el.addEventListener('click', () => show(open === id ? undefined : id))
      buttons.set(id, el)

      const panel = document.createElement('div')
      panel.className = 'map-toolbar__panel'
      panel.dataset.tool = id
      panel.hidden = true
      bar.appendChild(panel)
      panels.set(id, panel)
      return panel
    },
    addAction(id, label, title, onClick) {
      const el = button(id, label, title)
      el.addEventListener('click', () => onClick(el))
    },
    onToolClose(id, cb) {
      closeHandlers.set(id, cb)
    },
    close: () => show(undefined),
  }
}
